import {
    DataParams, FormResult, GameResult, LinkResult, ListFilters,
    ListHeader, ListType, QuestionResult, TeamResult, ThemeResult,
    UserResult
} from "../../../types/admin";
import {useAppDispatch, useAppSelector} from "../../../app/store";
import React, {useEffect, useMemo, useState, useCallback} from "react";
import {clearError, getFormExcel, getInviteLinkExcel, getListItems} from "../../../app/slices/adminList";
import {ParamKeyValuePair, useSearchParams} from "react-router-dom";
import {
    CheckCircleFill,
    ExclamationTriangleFill, FileEarmarkExcel,
    Plus,
    Search,
    XCircleFill
} from "react-bootstrap-icons";
import {ADD_USER, ADMIN_LANG} from "../../../settings";
import {Button, Col, Collapse, Container, Form, Row, Table} from "react-bootstrap";
import AdminLoader from "../AdminLoader";
import ListPagination from "./ListPagination";
import toast from "react-hot-toast";
import {formatDate, formatRelativeDate} from "../utils";
import AdminListLanguages from "./AdminListLanguages";
import {openModal as openThemeModal} from "../../../app/slices/modals/themeModal";
import {openModal as openTeamModal} from "../../../app/slices/modals/teamModal";
import {openModal as openFormModal} from "../../../app/slices/modals/formModal";
import {FilterRender, SortableHeader} from "./ListFilters";
import {openModal as openLinkModal} from "../../../app/slices/modals/linkModal";

type Props = {
    params: DataParams;
    headers: ListHeader[];
    listType: ListType;
    addItem: (type: ListType) => void;
    filters: ListFilters[];
};

const AdminList = ({params, headers, listType, addItem, filters}: Props) => {
    const dispatch = useAppDispatch();
    const {loaded, error, count, next, previous, results} = useAppSelector((state) => state.adminList);
    const {permissions} = useAppSelector((state) => state.profile);
    const [searchParams, setSearchParams] = useSearchParams();
    const sq = searchParams.get('sq') || '';

    const [filtersOpen, setFiltersOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState(sq);

    useEffect(() => {
        setSearchQuery(sq);
        dispatch(getListItems({listType, ...params}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(params), listType]);


    // Memoizing table headers to prevent unnecessary re-renders
    const tableHeaders = useMemo(() => {
        return headers.map((header: ListHeader) =>
            header.sortable ? (
                <SortableHeader key={header.key} label={header.label} sortValue={header.key}/>
            ) : (
                <th key={header.key}>{header.label}</th>
            )
        );
    }, [headers]);

    // Memoizing result rows to optimize rendering
    const resultRows = useMemo(() => {
        switch (listType) {
            case "games":
                return results.map((result) => {
                    const gameResult = result as GameResult;
                    const url = `/admin/games/${gameResult.game_id}/versions/${gameResult.version_id || gameResult.draft_id}/`;
                    return (
                        <tr key={gameResult.key}>
                            <td><a href={url}>{gameResult.name}</a></td>
                            <td>{gameResult.languages && <AdminListLanguages languages={gameResult.languages}/>}</td>
                            <td>{gameResult.pages}</td>
                            <td>{gameResult.played}</td>
                            <td>{formatDate(gameResult.modified)}</td>
                            <td>{formatDate(gameResult.created)}</td>
                        </tr>
                    );
                });
            case "users":
                return results.map((result) => {
                    const userResult = result as UserResult;
                    const userUrl = `/admin/users/${userResult.user_id}/`;
                    const lastSeen = userResult.last_seen ? formatRelativeDate(userResult.last_seen) : '';
                    const dateJoined = userResult.date_joined ? formatDate(userResult.date_joined) : '';
                    return (
                        <tr key={userResult.key}>
                            <td><a href={userUrl}>{userResult.full_name}</a></td>
                            <td>{userResult.email}</td>
                            <td>{userResult.is_active ? <CheckCircleFill className="text-success"/> :
                                <XCircleFill className="text-danger"/>}</td>
                            <td>{userResult.is_staff ? <CheckCircleFill className="text-success"/> :
                                <XCircleFill className="text-danger"/>}</td>
                            <td>{userResult.is_superuser ? <CheckCircleFill className="text-success"/> :
                                <XCircleFill className="text-danger"/>}</td>
                            <td>{dateJoined}</td>
                            <td>{lastSeen}</td>
                        </tr>
                    );
                });
            case "teams":
                return results.map((result) => {
                    const teamResult = result as TeamResult;
                    return (
                        <tr key={teamResult.key}>
                            <td><span className='link-span'
                                      onClick={() => handleEditTeam(teamResult.team_id)}>{teamResult.name}</span></td>
                            <td>{teamResult.users}</td>
                            <td><span className='link-span'
                                      onClick={() => handleCopyUrl(teamResult.url)}>{teamResult.url}</span></td>
                            <td>
                                {teamResult.domains && teamResult.domains.map((domain) => (
                                    <span key={`${teamResult.key}-${domain}`}
                                          className="badge list-badge bg-secondary me-1">
                                        {domain}
                                    </span>
                                ))}
                            </td>
                            <td>{formatDate(teamResult.created)}</td>
                        </tr>
                    );
                });
            case "themes":
                return results.map((result) => {
                    const themeResult = result as ThemeResult;
                    const imageUrl = themeResult.background_image?.thumbnail_url;
                    const imageRender = imageUrl ? (
                        <div className='theme-image-placeholder'>
                            <img alt={themeResult.name} src={imageUrl} width='70px' height='70px'/>
                        </div>
                    ) : (
                        <div className='theme-image-placeholder error'>
                            <ExclamationTriangleFill/>
                        </div>
                    );
                    return (
                        <tr key={themeResult.key}>
                            <td>{imageRender}</td>
                            <td><span className='link-span'
                                      onClick={() => handleEditTheme(themeResult.theme_id)}>{themeResult.name}</span>
                            </td>
                            <td>{themeResult.used_in ? <CheckCircleFill className="text-success"/> :
                                <XCircleFill className="text-danger"/>}</td>
                            <td>{formatDate(themeResult.modified)}</td>
                            <td>{formatDate(themeResult.created)}</td>
                        </tr>
                    );
                });
            case "questions":
                return results.map((result) => {
                    const questionResult = result as QuestionResult;
                    return (
                        <tr key={questionResult.key}>
                            <td><a href={`/admin/questions/${questionResult.question_id}`}>{questionResult.title}</a>
                            </td>
                            <td>{questionResult.type_display}</td>
                            <td>{questionResult.languages &&
                                <AdminListLanguages languages={questionResult.languages}/>}</td>
                            <td>{questionResult.played}</td>
                            <td>{
                                questionResult.game && (
                                    <a href={`/admin/games/${questionResult.game.game_id}/versions/${questionResult.game.version_id}`}>
                                        {questionResult.game.name}
                                    </a>
                                )}</td>
                            <td>{formatDate(questionResult.created)}</td>
                            <td>{formatDate(questionResult.modified)}</td>
                        </tr>
                    );
                });
            case "forms":
                return results.map((result) => {
                    const formResult = result as FormResult;
                    return (
                        <tr key={formResult.key}>
                            <td><span className='link-span'
                                      onClick={() => handleEditForm(formResult.form_id)}>{formResult.name}</span>
                            </td>
                            <td>{formResult.used_in ? <CheckCircleFill className="text-success"/> :
                                <XCircleFill className="text-danger"/>}</td>
                            <td>
                                {formResult.active_languages && formResult.active_languages.map((field, index) => (
                                    <span key={index} className="badge rounded-pill me-1 bg-success">
                                        {field}
                                    </span>
                                ))}
                            </td>
                            <td>
                                {formResult.form_fields_list && formResult.form_fields_list.map((field, index) => (
                                    <span key={index} className="badge list-badge bg-secondary me-1">
                                        {field}
                                    </span>
                                ))}
                            </td>
                            <td>{formatDate(formResult.created)}</td>
                            <td><span title={ADMIN_LANG.export_to_excel} className='cursor-pointer' onClick={() => handleExport(formResult.form_id)}><FileEarmarkExcel className='text-info'/></span></td>
                        </tr>
                    );
                });
            case "links":
                return results.map((result) => {
                    const linkResult = result as LinkResult;
                    const _user = linkResult.created_by ? (
                        <a href={`/admin/users/${linkResult.created_by.user_id}`}>
                            {linkResult.created_by.full_name}
                        </a>
                    ) : null;

                    return (
                        <tr key={linkResult.key}>
                            <td><span onClick={() => handleEditLink(linkResult.link_id)}
                                      className='link-span'>{linkResult.description}</span></td>
                            <td>{linkResult.game && linkResult.game.label}</td>
                            <td><span className='link-span'
                                      onClick={() => handleCopyUrl(linkResult.url)}>{linkResult.url}</span></td>
                            {/*<td>{linkResult.team &&*/}
                            {/*    <span className='badge list-badge bg-secondary'>{linkResult.team.label}</span>}</td>*/}
                            <td>{linkResult.usages} / {linkResult.max_usages === 0 ? '∞' : linkResult.max_usages}</td>
                            <td>{linkResult.expires && formatDate(linkResult.expires)}</td>
                            <td>{_user}</td>
                            <td>{formatDate(linkResult.created)}</td>
                        </tr>
                    );
                });
            default:
                return [];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listType, results]);

    const handleExport = useCallback((form_id: string) => {
        dispatch(getFormExcel(form_id));
    }, [dispatch]);

    // Event Handlers (Memoized)
    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    }, []);

    const handleFormSubmit = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        const entries: ParamKeyValuePair[] = [];
        searchParams.forEach((value, key) => {
            if (key !== 'sq') entries.push([key, value]);
        });
        entries.push(['sq', searchQuery]);
        setSearchParams(entries);
    }, [searchParams, searchQuery, setSearchParams]);

    const clearFilters = useCallback(() => {
        const entries: ParamKeyValuePair[] = [];
        searchParams.forEach((value, key) => {
            // Retain only 'sort_direction' and 'sort_value', clear all others
            if (key === 'sort_direction' || key === 'sort_value') {
                entries.push([key, value]);
            }
        });
        setSearchQuery('');
        setSearchParams(entries);
    }, [searchParams, setSearchParams]);

    const handleEditTheme = useCallback((theme_id: string) => {
        dispatch(openThemeModal({theme_id}));
    }, [dispatch]);

    const handleEditForm = useCallback((form_id: string) => {
        dispatch(openFormModal({form_id}));
    }, [dispatch]);

    const handleEditTeam = useCallback((team_id: string) => {
        dispatch(openTeamModal({team_id}));
    }, [dispatch]);

    const handleEditLink = useCallback((link_id: string) => {
        dispatch(openLinkModal({link_id}));
    }, [dispatch]);

    const handleCopyUrl = useCallback((url: string) => {
        if (url) {
            navigator.clipboard.writeText(url)
                .then(() => toast.success(ADMIN_LANG.url_copied, {position: 'top-right'}))
                .catch(() => toast.error(ADMIN_LANG.url_copy_failed, {position: 'top-right'}));
        }
    }, []);

    const isFilterActive = useCallback((params: DataParams, filters: string[]): boolean => {
        return filters.some((filter) => {
            const value = params[filter as keyof DataParams];
            return value !== undefined && value !== null && value !== '';
        }) || (params.sq !== undefined && params.sq !== null && params.sq !== '');
    }, []);

    const filtersActive = useMemo(() => isFilterActive(params, filters), [params, filters, isFilterActive]);

    if (error) {
        toast.error(error, {position: 'top-right'});
        dispatch(clearError());
    }

    const handleExportLinks = () => {
        dispatch(getInviteLinkExcel({listType, ...params}));
    }

    const addableTypes = useMemo(() => ['users', 'teams', 'themes', 'games', 'forms', 'links'], []);

    return (
        <Container>
            <Row className='pt-4 pb-4'>
                <Col xs={12} className='d-flex flex-wrap flex-md-nowrap align-items-center'>
                    {addableTypes.includes(listType) && (
                        <Button variant='success'
                                onClick={() => addItem(listType)}
                                className='me-2'
                                disabled={listType === 'users' && !permissions.includes(ADD_USER)}
                        >
                            <Plus/>
                            <span className="ms-2">{ADMIN_LANG.add_new}</span>
                        </Button>
                    )}
                    {filters.length > 0 && (
                        <Button
                            onClick={() => setFiltersOpen(!filtersOpen)}
                            aria-controls='extra-filters'
                            aria-expanded={filtersOpen}
                            variant='primary'>
                            {ADMIN_LANG.filter}
                        </Button>
                    )}
                    {listType === 'links' && (
                    <Button variant='info' className='ms-2'
                                        onClick={handleExportLinks}>{ADMIN_LANG.export}</Button>
                    )}
                    {filtersActive && (
                        <Button onClick={clearFilters} variant='outline-primary' className='ms-2'>
                            {ADMIN_LANG.clear_filters}
                        </Button>
                    )}
                </Col>
            </Row>
            <Row>
                <Form onSubmit={handleFormSubmit} method='get' className='list-filters col-12'>
                    <Row className='pb-3 gy-2'>
                        <Col xs={12} md={6} xl={4} className='search-input'>
                            <Form.Control type='text' value={searchQuery} onChange={handleSearchChange}
                                          placeholder={ADMIN_LANG.search}/>
                            <Button type='submit' className='search-button'><Search/></Button>
                        </Col>
                    </Row>
                </Form>
            </Row>
            <Row>
                <Col xs={12}>
                    <Collapse in={filtersOpen}>
                        <div>
                            <FilterRender filters={filters}/>
                            <Col className='pb-3'></Col>
                        </div>
                    </Collapse>
                </Col>
            </Row>

            <Row className='pb-4 position-relative'>
                {!loaded && <AdminLoader type='full'/>}
                <Col xs={12} className='pb-3'>
                    <div className='admin-card'>
                        <div className='table-container'>
                            <Table responsive hover>
                                <thead>
                                <tr>
                                    {tableHeaders}
                                </tr>
                                </thead>
                                <tbody>
                                {resultRows}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </Col>
                <ListPagination count={count} next={next} previous={previous}/>
            </Row>
        </Container>
    );
}

export default AdminList;

